import { useMemo } from "react";
import { money } from "../i18n/intl";
import { readParsedInvoiceReport } from "../utils/parsedInvoiceReport";

/**
 * G-66: what the parser found, shown to the person who uploaded the document.
 *
 * Every parser has always produced warnings and a totals audit, and
 * `runParseLocked` has always stored them in `Invoice.parsedPayload` — but
 * nothing read them. A totals mismatch, an unmatched product row, a line
 * skipped for want of a price: all written to Postgres, shipped to the
 * browser inside the preview response, and dropped. Making the checks visible
 * is what makes them checks.
 */
export function InvoiceParserReport({ payload }: { payload: unknown }) {
  const report = useMemo(() => readParsedInvoiceReport(payload), [payload]);
  const { warnings, totalsAudit } = report;

  if (warnings.length === 0 && !totalsAudit) return null;

  const bands = totalsAudit?.bands ?? [];
  // Three states, not two. `null` means the stored payload never recorded the
  // result — an invoice parsed before the check existed — and that is neither
  // agreement nor disagreement. Reading it as either one tells the reader
  // something the document cannot support.
  const disagrees =
    totalsAudit?.totalsAligned === false || bands.some((b) => b.aligned === false);
  const unchecked =
    (totalsAudit != null && totalsAudit.totalsAligned === null) ||
    bands.some((b) => b.aligned === null);
  const needsAttention = disagrees || unchecked || warnings.length > 0;
  const showBands = bands.length > 1 || disagrees;
  const matches = totalsAudit?.totalsAligned === true && !disagrees && !unchecked;

  return (
    <div
      className={needsAttention ? "alert warning" : "card"}
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}
    >
      <strong>{needsAttention ? "Parser checks — needs a look" : "Parser checks"}</strong>

      {totalsAudit ? (
        <div style={{ fontSize: 14 }}>
          {matches && totalsAudit.documentTotalEur != null ? (
            <strong>
              ✓ Item totals match the invoice{" "}
              {totalsAudit.documentTotalKind === "BASE"
                ? "subtotal"
                : totalsAudit.documentTotalKind === "GROSS"
                  ? "total including VAT"
                  : "stated total"}: {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(totalsAudit.documentTotalEur))}
            </strong>
          ) : (
          <>
          Lines add up to {money(totalsAudit.linesSumEur)}
          {totalsAudit.documentTotalEur != null ? (
            <>
              {" vs the document's "}
              {documentFigureName(totalsAudit.documentTotalKind)}{" "}
              {money(totalsAudit.documentTotalEur)}
              {alignmentText(totalsAudit.totalsAligned)}
              {/* G-121 S1: when the compared figure is the base, the paper's
                  VAT-inclusive total is a different number and is named as
                  such — never presented as the one the lines were checked
                  against. */}
              {totalsAudit.documentTotalKind === "BASE" &&
              totalsAudit.grandTotalEur != null
                ? ` · Total with IVA ${money(totalsAudit.grandTotalEur)}`
                : null}
            </>
          ) : (
            " — the document stated no subtotal to compare against."
          )}
          </>
          )}
        </div>
      ) : null}

      {showBands && bands.length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: 14 }}>
          {bands.map((band) => (
            <li key={band.ivaCode}>
              VAT band {band.ivaCode}
              {band.ivaCode === "R" ? " (flowers and plants)" : ""}
              {band.ivaCode === "G" ? " (everything else)" : ""}: lines{" "}
              {money(band.linesSumEur)} vs {money(band.documentBaseEur)}
              {alignmentText(band.aligned)}
            </li>
          ))}
        </ul>
      ) : null}

      {warnings.length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: 14 }}>
          {/* Index, not the text: two lines can produce the same message. */}
          {warnings.map((warning, i) => (
            <li key={`${i}-${warning}`}>{warning}</li>
          ))}
        </ul>
      ) : null}

      {needsAttention ? (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Nothing here blocks the commit. Check the lines below against the paper before
          you receive the delivery.
        </p>
      ) : null}
    </div>
  );
}

/**
 * `null` is NOT "no". An invoice stored before a given check existed simply
 * never recorded its result, and saying "they agree" there would assert a
 * comparison that never happened.
 */
function alignmentText(aligned: boolean | null): string {
  if (aligned === true) return " — they agree.";
  if (aligned === false) return " — they DISAGREE.";
  return " — not compared (this invoice was parsed before that check existed).";
}

/**
 * G-121 S1: which printed figure the lines were compared against. Parsers
 * from 1.8.0 say BASE or GROSS; an older payload, or a layout whose total
 * the parser could not classify, is named neutrally rather than guessed —
 * "stated total" claims nothing about IVA either way.
 */
function documentFigureName(kind: "BASE" | "GROSS" | null): string {
  if (kind === "BASE") return "base (before IVA)";
  if (kind === "GROSS") return "total (with IVA)";
  return "stated total";
}
